import React, { useState, useEffect, useCallback } from 'react';
import {
    Users,
    Search,
    Filter,
    UserCheck,
    Calendar,
    DollarSign,
    Eye,
    MoreVertical,
    AlertCircle,
    ChevronDown,
    GitBranch,
    X,
    ArrowLeft,
    Home,
    ChevronRight
} from 'lucide-react';
import { partnersService } from '../services/partnersService';
import Nt from './Nt';

const PartnersPage = ({ onNavigateToStructure }) => {
    const [partnersData, setPartnersData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [showPartnerModal, setShowPartnerModal] = useState(false);

    // Состояние для навигации
    const [currentPartnerId, setCurrentPartnerId] = useState(null); // null = мои партнеры
    const [navigationHistory, setNavigationHistory] = useState([]);

    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        level: 'all',
        sort_by: 'created_at',
        sort_direction: 'desc',
        per_page: 10
    });
    const [currentPage, setCurrentPage] = useState(1);

    // Загрузка данных партнеров
    const loadPartnersData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await partnersService.getPartners({
                ...filters,
                page: currentPage, // ИСПРАВЛЕНО: используем page вместо current_page
                partner_id: currentPartnerId // Добавляем ID партнера для просмотра его команды
            });

            if (response.success) {
                setPartnersData(response.data);
            } else {
                setError(response.message || 'Ошибка загрузки данных');
            }

        } catch (error) {
            console.error('Ошибка при загрузке данных партнеров:', error);
            setError('Произошла ошибка при загрузке данных');
        } finally {
            setIsLoading(false);
        }
    }, [filters, currentPage, currentPartnerId]);

    useEffect(() => {
        loadPartnersData();
    }, [loadPartnersData]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1); // Сброс на первую страницу при изменении фильтров
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { label: 'Активен', class: 'bg-green-100 text-green-800' },
            inactive: { label: 'Неактивен', class: 'bg-red-100 text-red-800' },
            pending: { label: 'Ожидает', class: 'bg-yellow-100 text-yellow-800' }
        };

        const config = statusConfig[status] || statusConfig.pending;
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>
                {config.label}
            </span>
        );
    };

    const getLevelBadge = (level) => {
        if (!level) return <span className="text-gray-400 text-xs">-</span>;

        const levelColors = {
            'Бронзовый': 'text-yellow-600',
            'Серебряный': 'text-gray-500',
            'Золотой': 'text-yellow-500'
        };

        return (
            <span className={`font-medium text-xs ${levelColors[level] || 'text-gray-600'}`}>
                {level}
            </span>
        );
    };

    // Навигация к команде конкретного партнера
    const viewPartnerStructure = (partnerId, partnerName) => {
        console.log('Переход к партнеру:', { partnerId, partnerName, currentPartnerId });

        // Не переходим, если уже смотрим на этого же партнера
        if (currentPartnerId === partnerId) {
            console.log('Уже просматриваем этого партнера');
            return;
        }

        // Добавляем ТЕКУЩЕЕ состояние в историю перед переходом
        const currentPartnerData = partnersData?.current_partner;

        if (currentPartnerData) {
            // Добавляем текущего партнера в историю
            setNavigationHistory(prev => [...prev, {
                id: currentPartnerId,
                name: currentPartnerData.full_name || currentPartnerData.login,
                login: currentPartnerData.login
            }]);
        } else if (currentPartnerId === null && navigationHistory.length === 0) {
            // Если мы на корневом уровне и история пуста, добавляем "Мои партнеры"
            setNavigationHistory(prev => [...prev, {
                id: null,
                name: 'Мои партнеры',
                login: null
            }]);
        }

        // Переходим к новому партнеру
        setCurrentPartnerId(partnerId);
        setCurrentPage(1);

        // Сброс фильтров при переходе к новому партнеру
        setFilters({
            search: '',
            status: 'all',
            level: 'all',
            sort_by: 'created_at',
            sort_direction: 'desc',
            per_page: 10
        });

        console.log('Переход выполнен к:', partnerId);
    };

    // Возврат назад по истории
    const goBack = () => {
        if (navigationHistory.length > 0) {
            const previousPartner = navigationHistory[navigationHistory.length - 1];
            console.log('Возврат назад к:', previousPartner);
            setNavigationHistory(prev => prev.slice(0, -1));
            setCurrentPartnerId(previousPartner.id);
            setCurrentPage(1);
        }
    };

    // Переход к корню (мои партнеры)
    const goToRoot = () => {
        console.log('Переход к корню');
        setCurrentPartnerId(null);
        setNavigationHistory([]);
        setCurrentPage(1);
    };

    // Переход к конкретному элементу в истории
    const navigateToHistoryItem = (index) => {
        console.log('Переход к элементу истории:', { index, navigationHistory });

        if (index === 0) {
            // Переход к корню (Мои партнеры)
            goToRoot();
        } else {
            // Переход к элементу истории
            const targetItem = navigationHistory[index - 1];
            console.log('Целевой элемент:', targetItem);
            setNavigationHistory(prev => prev.slice(0, index));
            setCurrentPartnerId(targetItem.id);
            setCurrentPage(1);
        }
    };

    // Просмотр детальной информации о партнере
    const viewPartnerDetails = async (partnerId) => {
        try {
            const response = await partnersService.getPartner(partnerId);
            if (response.success) {
                setSelectedPartner(response.data.partner);
                setShowPartnerModal(true);
            } else {
                alert('Ошибка загрузки данных партнера');
            }
        } catch (error) {
            console.error('Ошибка при загрузке партнера:', error);
            alert('Произошла ошибка при загрузке данных');
        }
    };

    // Хлебные крошки навигации
    const NavigationBreadcrumbs = () => (
        <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
            {/* Универсальная версия с переносом для всех экранов */}
            <div className="space-y-3">
                {/* Хлебные крошки */}
                <div className="flex flex-wrap items-center gap-1 text-sm">
                    <button
                        onClick={goToRoot}
                        className={`flex items-center space-x-1 px-2 py-1 rounded transition-colors whitespace-nowrap ${
                            currentPartnerId === null
                                ? 'bg-blue-100 text-blue-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <Home size={16} />
                        <span>Мои партнеры</span>
                    </button>

                    {navigationHistory.map((item, index) => (
                        <div key={`nav-${index}-${item.id}`} className="flex items-center gap-1">
                            <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                            <button
                                onClick={() => navigateToHistoryItem(index + 1)}
                                className="px-2 py-1 rounded text-gray-600 hover:bg-gray-100 transition-colors whitespace-nowrap max-w-[120px] sm:max-w-[180px] truncate text-sm"
                                title={`Перейти к: ${item.name}`}
                            >
                                {item.name}
                            </button>
                        </div>
                    ))}

                    {partnersData?.current_partner && currentPartnerId !== null && (
                        <div className="flex items-center gap-1">
                            <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                            <span
                                translate="no" className="notranslate px-2 py-1 bg-blue-100 text-blue-700 font-medium rounded whitespace-nowrap max-w-[120px] sm:max-w-[180px] truncate text-sm"
                                title={partnersData.current_partner.full_name || partnersData.current_partner.login}
                            >
                                {partnersData.current_partner.full_name || partnersData.current_partner.login}
                            </span>
                        </div>
                    )}
                </div>

                {/* Кнопка назад */}
                {navigationHistory.length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                        <button
                            onClick={goBack}
                            className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 transition-colors px-2 py-1 rounded hover:bg-blue-50"
                        >
                            <ArrowLeft size={16} />
                            <span>Назад к {navigationHistory[navigationHistory.length - 1]?.name}</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    // Модальное окно с информацией о партнере
    const PartnerModal = () => {
        if (!selectedPartner) return null;

        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                    {/* Заголовок */}
                    <div className="bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-12 h-12 rounded-full bg-white/20 overflow-hidden mr-3">
                                    {selectedPartner.avatar ? (
                                        <img src={selectedPartner.avatar} alt="Avatar" className="w-full h-full object-cover"/>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Users className="w-6 h-6 text-white" />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <h3 translate="no" className="notranslate text-xl font-semibold">
                                        {selectedPartner.first_name && selectedPartner.last_name
                                            ? `${selectedPartner.first_name} ${selectedPartner.last_name}`
                                            : selectedPartner.login}
                                    </h3>
                                    <p className="text-white/80"><Nt>{selectedPartner.login}</Nt></p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowPartnerModal(false)}
                                className="text-white/80 hover:text-white transition-colors"
                            >
                                <X size={24}/>
                            </button>
                        </div>
                    </div>

                    {/* Контент */}
                    <div className="p-6 space-y-4">
                        {/* Статус и уровень */}
                        <div className="flex items-center justify-between">
                            {getStatusBadge(selectedPartner.status)}
                        </div>

                        {/* Контактная информация */}
                        {selectedPartner.email && (
                            <div>
                                <p className="text-sm text-gray-500">Email</p>
                                <p className="font-medium">{selectedPartner.email}</p>
                            </div>
                        )}

                        {selectedPartner.phone && (
                            <div>
                                <p className="text-sm text-gray-500">Телефон</p>
                                <p className="font-medium">{selectedPartner.phone}</p>
                            </div>
                        )}

                        {/* Статистика команды */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                                <p className="text-2xl font-bold text-blue-primary">{selectedPartner.partners_count || 0}</p>
                                <p className="text-xs text-gray-500">Всего партнеров</p>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                                <p className="text-2xl font-bold text-green-600">{selectedPartner.active_partners_count || 0}</p>
                                <p className="text-xs text-gray-500">Активных</p>
                            </div>
                        </div>

                        {/* Даты */}
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Регистрация:</span>
                                <span className="text-sm font-medium">{formatDate(selectedPartner.created_at)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Последняя активность:</span>
                                <span className="text-sm font-medium">{formatDate(selectedPartner.last_activity)}</span>
                            </div>
                        </div>

                        {/* Реферальная информация */}

                    </div>

                    {/* Кнопки действий */}
                    <div className="p-6 pt-0 space-y-3">
                        <button
                            onClick={() => {
                                viewPartnerStructure(selectedPartner.id, selectedPartner.full_name || selectedPartner.login);
                                setShowPartnerModal(false);
                            }}
                            className="w-full bg-gradient-to-r from-blue-primary to-blue-primary/80 text-white py-3 rounded-lg hover:shadow-lg transition-all duration-300 font-medium flex items-center justify-center space-x-2"
                        >
                            <GitBranch size={18} />
                            <span>Посмотреть команду</span>
                        </button>
                        <button
                            onClick={() => setShowPartnerModal(false)}
                            className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Компонент карточки партнера для мобильных
    const PartnerCard = ({ partner }) => (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
            <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                        {partner.avatar ? (
                            <img
                                src={partner.avatar}
                                alt="Avatar"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <Users className="w-5 h-5 text-gray-400" />
                            </div>
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 translate="no" className="notranslate font-medium text-gray-900 truncate">
                            {partner.first_name && partner.last_name
                                ? `${partner.first_name} ${partner.last_name}`
                                : partner.login}
                        </h3>
                        <p className="text-sm text-gray-500"><Nt>{partner.login}</Nt></p>
                    </div>
                </div>
                <button
                    onClick={() => viewPartnerDetails(partner.id)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                >
                    <MoreVertical className="w-4 h-4" />
                </button>
            </div>

            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    {getStatusBadge(partner.status)}
                </div>
                <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                        {partner.partners_count || 0}/{partner.active_partners_count || 0}
                    </div>
                    <div className="text-xs text-gray-500">Команда</div>
                </div>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Рег: {formatDate(partner.created_at)}</span>
                <span>Активность: {formatDate(partner.last_activity)}</span>
            </div>

            <div className="flex space-x-2">
                <button
                    onClick={() => viewPartnerStructure(partner.id, partner.first_name && partner.last_name ? `${partner.first_name} ${partner.last_name}` : partner.login)}
                    className="flex-1 bg-blue-600 text-white py-2 px-3 rounded text-sm hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1"
                >
                    <GitBranch className="w-4 h-4" />
                    <span>Команда</span>
                </button>
                <button
                    onClick={() => viewPartnerDetails(partner.id)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded text-sm hover:bg-gray-200 transition-colors flex items-center justify-center space-x-1"
                >
                    <Eye className="w-4 h-4" />
                    <span>Детали</span>
                </button>
            </div>
        </div>
    );

    const ErrorComponent = () => (
        <div className="text-center py-8 px-4">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ошибка загрузки</h3>
            <p className="text-gray-500 mb-4 text-sm">{error}</p>
            <button
                onClick={loadPartnersData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
                Попробовать снова
            </button>
        </div>
    );

    const LoadingComponent = () => (
        <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
    );

    if (isLoading && !partnersData) {
        return <LoadingComponent />;
    }

    if (error && !partnersData) {
        return <ErrorComponent />;
    }

    const { current_partner, inviter, partners, pagination, team_stats } = partnersData || {};

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="w-full max-w-full px-4 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6">

                {/* Навигационные хлебные крошки */}
                <NavigationBreadcrumbs />

                {/* Заголовок */}
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
                                {currentPartnerId === null ? 'Моя команда' : 'Команда партнера'}
                            </h1>
                            <p className="text-gray-600 text-sm mt-1">
                                {currentPartnerId === null
                                    ? 'Управление партнерской сетью'
                                    : <>Просмотр команды: <Nt>{current_partner?.full_name || current_partner?.login}</Nt></>
                                }
                            </p>
                        </div>

                        {inviter && (
                            <div className="text-left sm:text-right mt-3 sm:mt-0">
                                <p className="text-xs text-gray-500 mb-1">Наставник:</p>
                                <p translate="no" className="notranslate font-medium text-gray-800 text-sm">
                                    {inviter.first_name && inviter.last_name
                                        ? `${inviter.first_name} ${inviter.last_name}`
                                        : inviter.login}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Информация о текущем партнере */}
                    {current_partner && (
                        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden">
                                {current_partner.avatar ? (
                                    <img
                                        src={current_partner.avatar}
                                        alt="Avatar"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Users className="w-5 h-5 text-gray-400" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 translate="no" className="notranslate font-semibold text-gray-900 truncate">
                                    {current_partner.first_name && current_partner.last_name
                                        ? `${current_partner.first_name} ${current_partner.last_name}`
                                        : current_partner.login}
                                </h3>
                                <p className="text-sm text-gray-500"><Nt>{current_partner.login}</Nt></p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Статистика команды */}
                {team_stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                            <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <p className="text-lg sm:text-2xl font-bold text-gray-900">{team_stats.total || 0}</p>
                            <p className="text-xs text-gray-500">Всего</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                            <UserCheck className="w-6 h-6 text-green-600 mx-auto mb-2" />
                            <p className="text-lg sm:text-2xl font-bold text-gray-900">{team_stats.active || 0}</p>
                            <p className="text-xs text-gray-500">Активных</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                            <Calendar className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                            <p className="text-lg sm:text-2xl font-bold text-gray-900">{team_stats.new_this_month || 0}</p>
                            <p className="text-xs text-gray-500">За месяц</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                            <DollarSign className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                            <p className="text-lg sm:text-2xl font-bold text-gray-900">{team_stats.earnings_from_team || 0}</p>
                            <p className="text-xs text-gray-500">Заработано</p>
                        </div>
                    </div>
                )}

                {/* Поиск и фильтры */}
                <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="space-y-3">
                        {/* Поиск */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Поиск партнеров..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                        </div>

                        {/* Кнопка фильтров на мобильных */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="sm:hidden w-full flex items-center justify-center space-x-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <Filter className="w-4 h-4" />
                            <span>Фильтры</span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Фильтры */}
                        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${showFilters ? 'block' : 'hidden sm:grid'}`}>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            >
                                <option value="created_at-desc">Сначала новые</option>
                                <option value="created_at-asc">Сначала старые</option>
                                <option value="last_activity-desc">По активности ↓</option>
                                <option value="last_activity-asc">По активности ↑</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Список партнеров */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                            Партнеры {pagination && `(${pagination.total})`}
                        </h3>
                        {isLoading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        )}
                    </div>

                    {partners && partners.length > 0 ? (
                        <>
                            {/* Мобильная версия - карточки */}
                            <div className="sm:hidden p-4 space-y-3">
                                {partners.map((partner) => (
                                    <PartnerCard key={partner.id} partner={partner} />
                                ))}
                            </div>

                            {/* Десктопная версия - таблица */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Партнер
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Статус
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Команда
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Регистрация
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Активность
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                            Действия
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {partners.map((partner) => (
                                        <tr key={partner.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center">
                                                    <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
                                                        {partner.avatar ? (
                                                            <img
                                                                src={partner.avatar}
                                                                alt="Avatar"
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <Users className="w-4 h-4 text-gray-400" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="ml-3">
                                                        <div translate="no" className="notranslate text-sm font-medium text-gray-900">
                                                            {partner.first_name && partner.last_name
                                                                ? `${partner.first_name} ${partner.last_name}`
                                                                : partner.login}
                                                        </div>
                                                        <div className="text-xs text-gray-500"><Nt>{partner.login}</Nt></div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {getStatusBadge(partner.status)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                <div className="flex items-center space-x-2">
                                                    <span>{partner.partners_count || 0}</span>
                                                    <span className="text-gray-400">/</span>
                                                    <span className="text-green-600">{partner.active_partners_count || 0}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                {formatDate(partner.created_at)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                {formatDate(partner.last_activity)}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => viewPartnerStructure(partner.id, partner.first_name && partner.last_name ? `${partner.first_name} ${partner.last_name}` : partner.login)}
                                                        className="text-blue-600 hover:text-blue-900 p-1"
                                                        title="Посмотреть команду"
                                                    >
                                                        <GitBranch size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => viewPartnerDetails(partner.id)}
                                                        className="text-green-600 hover:text-green-900 p-1"
                                                        title="Просмотр деталей"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => viewPartnerDetails(partner.id)}
                                                        className="text-gray-600 hover:text-gray-900 p-1"
                                                        title="Дополнительные действия"
                                                    >
                                                        <MoreVertical size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-8 px-4">
                            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Партнеров пока нет</h3>
                            <p className="text-gray-500 text-sm">
                                {currentPartnerId === null
                                    ? 'Пригласите первых партнеров, используя вашу реферальную ссылку'
                                    : 'У этого партнера пока нет команды'
                                }
                            </p>
                        </div>
                    )}

                    {/* ИСПРАВЛЕННАЯ ПАГИНАЦИЯ */}
                    {pagination && pagination.last_page > 1 && (
                        <div className="px-4 py-3 border-t border-gray-100">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                                <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                                    Показано {pagination.from || 0} - {pagination.to || 0} из {pagination.total} записей
                                </div>
                                <div className="flex items-center justify-center space-x-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={pagination.current_page === 1 || isLoading}
                                        className="px-3 py-1 border rounded text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                    >
                                        Назад
                                    </button>
                                    <span className="text-xs sm:text-sm text-gray-500">
                                        {pagination.current_page} / {pagination.last_page}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                        disabled={pagination.current_page === pagination.last_page || isLoading}
                                        className="px-3 py-1 border rounded text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                    >
                                        Вперед
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Модальное окно с информацией о партнере */}
            {showPartnerModal && <PartnerModal />}
        </div>
    );
};

export default PartnersPage;
